import React, { useEffect, useMemo, useState } from 'react';
import { FaPlus, FaRegSave, FaSearch } from 'react-icons/fa';
import './RoleConfiguration.css';

const API = import.meta.env.VITE_API_URL;

async function fetchJSON(url, opts = {}) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = {
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
    ...(user?.userid ? { userid: user.userid } : {}),
  };
  const res = await fetch(url, { credentials: 'include', ...opts, headers });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.message) msg = body.message;
      if (body?.error) msg = body.error;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

function normalizeRole(role = {}) {
  return {
    roleid: Number(role.roleid || 0) || 0,
    name: String(role.name || ''),
    description: String(role.description || ''),
    active: Number(role.active ?? 1) === 1,
    is_system: Number(role.is_system || 0) === 1,
    rights: Array.isArray(role.rights) ? role.rights.map((r) => Number(r.id || r.rightid || 0)).filter(Boolean) : [],
  };
}

function rightGroupName(rightname = '') {
  const [prefix = 'misc'] = String(rightname).split('.');
  return prefix
    .split(/[_-]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function RoleConfiguration() {
  const [roles, setRoles] = useState([]);
  const [rights, setRights] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(0);
  const [editor, setEditor] = useState(null);
  const [roleSearch, setRoleSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Role Configuration', subtitle: '' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, []);

  const loadAll = async (preferredRoleId = 0) => {
    setLoading(true);
    setStatus('');
    try {
      const [rolesRes, rightsRes] = await Promise.all([
        fetchJSON(`${API}/api/admin/rbac/roles?includeRights=1`),
        fetchJSON(`${API}/api/admin/rbac/rights`),
      ]);
      const nextRoles = Array.isArray(rolesRes?.roles) ? rolesRes.roles.map(normalizeRole) : [];
      const nextRights = Array.isArray(rightsRes?.rights) ? rightsRes.rights : [];
      setRoles(nextRoles);
      setRights(nextRights);

      const desiredRoleId = Number(preferredRoleId || selectedRoleId || nextRoles[0]?.roleid || 0) || 0;
      const selected = nextRoles.find((role) => Number(role.roleid) === desiredRoleId) || nextRoles[0] || null;
      setSelectedRoleId(Number(selected?.roleid || 0) || 0);
      setEditor(selected ? { ...selected, rights: [...selected.rights] } : null);
    } catch (error) {
      setRoles([]);
      setRights([]);
      setSelectedRoleId(0);
      setEditor(null);
      setStatus(error.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredRoles = useMemo(() => {
    const term = String(roleSearch || '').trim().toLowerCase();
    if (!term) return roles;
    return roles.filter((role) => {
      return (
        String(role.name || '').toLowerCase().includes(term) ||
        String(role.description || '').toLowerCase().includes(term)
      );
    });
  }, [roleSearch, roles]);

  const groupedRights = useMemo(() => {
    const term = String(rightSearch || '').trim().toLowerCase();
    const filtered = (Array.isArray(rights) ? rights : []).filter((right) => {
      if (!term) return true;
      return String(right?.rightname || '').toLowerCase().includes(term);
    });

    const groups = new Map();
    filtered.forEach((right) => {
      const key = rightGroupName(right?.rightname);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(right);
    });

    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([group, items]) => ({
        group,
        items: [...items].sort((a, b) => String(a.rightname || '').localeCompare(String(b.rightname || ''))),
      }));
  }, [rightSearch, rights]);

  const selectRole = (roleid) => {
    const nextRoleId = Number(roleid || 0) || 0;
    const selected = roles.find((role) => Number(role.roleid) === nextRoleId) || null;
    setSelectedRoleId(nextRoleId);
    setEditor(selected ? { ...selected, rights: [...selected.rights] } : null);
    setStatus('');
  };

  const createRoleDraft = () => {
    setSelectedRoleId(0);
    setEditor({
      roleid: 0,
      name: '',
      description: '',
      active: true,
      is_system: false,
      rights: [],
    });
    setStatus('');
  };

  const toggleRight = (rightId) => {
    const rightid = Number(rightId || 0) || 0;
    if (!rightid) return;
    setEditor((prev) => {
      if (!prev) return prev;
      const current = new Set(Array.isArray(prev.rights) ? prev.rights : []);
      if (current.has(rightid)) current.delete(rightid);
      else current.add(rightid);
      return { ...prev, rights: Array.from(current).sort((a, b) => a - b) };
    });
  };

  const saveRole = async () => {
    if (!editor) return;
    const name = String(editor.name || '').trim();
    if (!name) {
      setStatus('Role name is required');
      return;
    }

    setSaving(true);
    setStatus('');
    try {
      let roleid = Number(editor.roleid || 0) || 0;
      const payload = {
        name,
        description: String(editor.description || '').trim(),
        active: !!editor.active,
      };

      if (!roleid) {
        const created = await fetchJSON(`${API}/api/admin/rbac/roles`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        roleid = Number(created?.roleid || 0) || 0;
      } else {
        await fetchJSON(`${API}/api/admin/rbac/roles/${roleid}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }

      await fetchJSON(`${API}/api/admin/rbac/roles/${roleid}/rights`, {
        method: 'PUT',
        body: JSON.stringify({
          rightIds: Array.isArray(editor.rights) ? editor.rights : [],
        }),
      });

      await loadAll(roleid);
      setStatus('Role saved');
    } catch (error) {
      setStatus(error.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="role-config-page">
      <div className="role-config-shell">
        <aside className="role-config-list">
          <div className="role-config-toolbar">
            <label className="role-config-search">
              <FaSearch />
              <input
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
                placeholder="Search roles"
              />
            </label>
            <button type="button" className="role-config-create" onClick={createRoleDraft}>
              <FaPlus />
              <span>New Role</span>
            </button>
          </div>

          <div className="role-config-rolelist">
            {filteredRoles.map((role) => (
              <button
                type="button"
                key={role.roleid}
                className={`role-config-rolecard ${Number(selectedRoleId) === Number(role.roleid) ? 'selected' : ''}`}
                onClick={() => selectRole(role.roleid)}
              >
                <div className="role-config-rolehead">
                  <strong>{role.name}</strong>
                  <span className={`role-config-badge ${role.active ? 'active' : 'inactive'}`}>
                    {role.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="role-config-roledesc">
                  {role.description || 'No description'}
                </div>
              </button>
            ))}
            {!filteredRoles.length && (
              <div className="role-config-empty">No roles match the current filter.</div>
            )}
          </div>
        </aside>

        <section className="role-config-editor">
          {!editor ? (
            <div className="role-config-emptypanel">
              {loading ? 'Loading roles…' : 'Select a role or create a new one.'}
            </div>
          ) : (
            <>
              <div className="role-config-header">
                <div>
                  <h2>{editor.roleid ? editor.name || 'Untitled Role' : 'New Role'}</h2>
                  <div className="role-config-meta">
                    {editor.is_system ? 'System role' : 'Custom role'}
                  </div>
                </div>
                <button type="button" className="role-config-save" onClick={saveRole} disabled={saving}>
                  <FaRegSave />
                  <span>{saving ? 'Saving…' : 'Save Role'}</span>
                </button>
              </div>

              <div className="role-config-form">
                <label>
                  <span>Role Name</span>
                  <input
                    value={editor.name}
                    onChange={(e) => setEditor((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Warehouse Supervisor"
                  />
                </label>

                <label>
                  <span>Description</span>
                  <textarea
                    rows="3"
                    value={editor.description}
                    onChange={(e) => setEditor((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this role should be able to do."
                  />
                </label>

                <label className="role-config-checkbox">
                  <input
                    type="checkbox"
                    checked={!!editor.active}
                    onChange={(e) => setEditor((prev) => ({ ...prev, active: e.target.checked }))}
                  />
                  <span>Role is active</span>
                </label>
              </div>

              <div className="role-config-rights-head">
                <h3>Rights</h3>
                <label className="role-config-search">
                  <FaSearch />
                  <input
                    value={rightSearch}
                    onChange={(e) => setRightSearch(e.target.value)}
                    placeholder="Filter rights"
                  />
                </label>
              </div>

              <div className="role-config-rights">
                {groupedRights.map(({ group, items }) => (
                  <section className="role-config-group" key={group}>
                    <div className="role-config-group-title">{group}</div>
                    <div className="role-config-group-items">
                      {items.map((right) => {
                        const rightid = Number(right.id || 0) || 0;
                        const checked = Array.isArray(editor.rights) && editor.rights.includes(rightid);
                        return (
                          <label className="role-config-right" key={rightid}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRight(rightid)}
                            />
                            <span>{right.rightname}</span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                ))}
                {!groupedRights.length && (
                  <div className="role-config-empty">No rights match the current filter.</div>
                )}
              </div>

              {status && (
                <div className={`role-config-status ${status === 'Role saved' ? 'success' : 'error'}`}>
                  {status}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
